import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CollectionsIcon from '@mui/icons-material/Collections';

import { useLab } from '../../../queries';
import { ThemeMenu } from './ThemeMenu';
import { CreditChip } from '../../../credits/CreditChip';
import { SettingsMenu } from '../../../components/common/SettingsMenu';
import { AppSwitcher } from './AppSwitcher';

interface Props {
  labId: string;
  studioId: string;
}

/**
 * Top bar per UI spec §4.3. Left: lab title + studio dropdown (driven by
 * `useLab(labId).studios`). Right: theme menu, gallery link.
 *
 * Undo / redo land in F-history once we can properly mirror BE
 * history state into the local canvas carousel — they're not in this
 * slice on purpose. The carousel's prev/next arrows handle simple
 * navigation in the meantime.
 *
 * Studio dropdown navigates by URL; ImageLab.tsx's existing `studioId`
 * URL param + studio-switch effect handles the canvas-state reset.
 */
export const TopBar: React.FC<Props> = ({ labId, studioId }) => {
  const navigate = useNavigate();
  const labQuery = useLab(labId);
  const lab = labQuery.data;
  const studios = lab?.studios ?? [];

  const handleStudioChange = (next: string) => {
    if (next === studioId) return;
    navigate(`/labs/${labId}/${next}`);
  };

  return (
    <>
    <Box
      component="header"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 0.75,
        bgcolor: 'background.paper',
        minHeight: 48,
      }}
      aria-label="Lab top bar"
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
        {/* The lab identity doubles as the app switcher — clicking it opens the
            grid of labs + surfaces (Google apps-grid pattern). */}
        <AppSwitcher labId={labId} labName={lab?.name ?? 'Image Lab'} />
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        {studios.length > 0 ? (
          <FormControl size="small" variant="standard" sx={{ minWidth: 160 }}>
            <Select
              value={studios.some(s => s.id === studioId) ? studioId : ''}
              displayEmpty
              onChange={e => handleStudioChange(e.target.value)}
              disableUnderline
              sx={{
                fontSize: 14,
                fontWeight: 500,
                '& .MuiSelect-select': { py: 0.5, pl: 1, pr: 3 },
              }}
            >
              {studios.map(s => (
                <MenuItem key={s.id} value={s.id} dense>
                  <Box>
                    <Typography variant="body2">{s.name}</Typography>
                    {s.description && (
                      <Typography variant="caption" color="text.secondary">
                        {s.description}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {studioId}
          </Typography>
        )}
      </Stack>

      <Box sx={{ flex: 1 }} />

      <Stack direction="row" spacing={0.5} alignItems="center">
        <CreditChip />
        <ThemeMenu />
        <SettingsMenu />
        <Tooltip title="Asset gallery">
          <IconButton size="small" onClick={() => navigate('/assets')}>
            <CollectionsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
    {/* Branded accent — a thin theme-gradient strip that doubles as the
        TopBar's separator, tying every lab shell to the active theme. */}
    <Box
      aria-hidden
      sx={{
        height: 3,
        flexShrink: 0,
        background: theme =>
          `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
      }}
    />
    </>
  );
};
